/*
 * Daro segments: which segment a vehicle belongs to (window.DaroSegments, used by the vehicle finder,
 * assets/daro-finder.js, to offer only the current segment's vehicles), and search forms scoped to the chosen
 * vehicle or the current segment. Config comes from snippets/segment-data.liquid (#DaroSegments).
 *
 * Rules: "Ferrari" (whole make), "Audi > R8" (exact model), "Mercedes-Benz > AMG GT*" (model prefix).
 * A model rule beats a whole-make rule in another segment. Makes with no rule are hidden everywhere.
 */
(() => {
  const configEl = document.getElementById('DaroSegments');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const norm = (s) => String(s || '').replace(/["']/g, '').trim().toLowerCase();
  // Only the server-resolved segment limits the YMM and search. The colour fallback restored from
  // localStorage (cart, plain search, /collections/all) must not restrict a neutral page.
  const current = config.current || '';

  // Parse rules into { handle, name, url, makes: Set, models: [{ make, model, prefix }] }
  const segments = (config.segments || []).map((seg) => {
    const makes = new Set();
    const models = [];
    (seg.rules || []).forEach((rule) => {
      if (!rule) return;
      const [make, model] = rule.split('>');
      if (model === undefined) {
        makes.add(norm(make));
      } else {
        const raw = model.trim();
        const prefix = raw.endsWith('*');
        models.push({ make: norm(make), model: norm(prefix ? raw.slice(0, -1) : raw), prefix });
      }
    });
    return { ...seg, makes, models };
  });
  if (!segments.length) return;

  const modelRuleMatches = (rule, make, model) =>
    rule.make === make && (rule.prefix ? model.startsWith(rule.model) : model === rule.model);

  // Which segment does this vehicle belong to? Model rules first, then whole makes.
  function segmentFor(make, model) {
    make = norm(make);
    model = norm(model);
    if (model) {
      const byModel = segments.find((s) => s.models.some((r) => modelRuleMatches(r, make, model)));
      if (byModel) return byModel;
    }
    return segments.find((s) => s.makes.has(make)) || null;
  }

  const activeSegments = () => {
    const seg = segments.find((s) => s.handle === current);
    return seg ? [seg] : segments; // outside a segment: union of all (hides unassigned makes)
  };

  function makeAllowed(make) {
    make = norm(make);
    return activeSegments().some((s) => s.makes.has(make) || s.models.some((r) => r.make === make));
  }

  function modelAllowed(make, model) {
    const owner = segmentFor(make, model);
    if (!owner) {
      // Make appears only via model rules and this model matches none of them
      return false;
    }
    return activeSegments().includes(owner);
  }

  // Search forms, as they're sent: with a chosen vehicle, only parts that fit it (its v: tag, plus the v-many
  // products sections/main-search.liquid checks); otherwise, on a segment page, that segment plus universal
  // parts. Uses the hidden Tag filter (Search & Discovery; snippets/facets.liquid never shows it).
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      let path = '';
      try {
        path = new URL(form.getAttribute('action') || '', location.href).pathname;
      } catch (e) {
        return;
      }
      if (!/\/search\/?$/.test(path)) return;
      form.querySelectorAll('input[data-daro-scope]').forEach((input) => input.remove());
      const key = window.DaroVehicle && window.DaroVehicle.key;
      let values = [];
      if (key) values = [`v:${key}`, 'v-many'];
      else if (current && !form.querySelector('input[name="filter.p.tag"]')) values = [`seg-${current}`, 'seg-universal'];
      const add = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.dataset.daroScope = '';
        form.appendChild(input);
      };
      values.forEach((value) => add('filter.p.tag', value));
      if (key && !form.querySelector('[name="type"]')) add('type', 'product');
    },
    true
  );

  // Product page: Daro blocks marked [data-daro-swap][id] refresh from the new variant's HTML
  document.addEventListener('variant:update', (event) => {
    const source = event?.detail?.data?.html;
    if (!source || typeof source.querySelector !== 'function') return;
    document.querySelectorAll('[data-daro-swap][id]').forEach((el) => {
      const next = source.querySelector(`#${CSS.escape(el.id)}`);
      if (next) el.innerHTML = next.innerHTML;
    });
  });

  // For the vehicle finder, and a debug hook: DaroSegments.segmentFor('Audi', 'R8') in the console
  window.DaroSegments = { current, segmentFor, makeAllowed, modelAllowed };
})();
